import React, { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import PortalTooltip from './PortalTooltip';

const parseNum = (str) => {
  if (!str) return 0;
  if (typeof str === 'number') return str;
  const num = parseFloat(String(str).replace(/,/g, '').replace(/\$|B|M|K|%/g, (match) => {
    switch (match) {
      case 'B': return 'e9';
      case 'M': return 'e6';
      case 'K': return 'e3';
      case '%': return '';
      default: return '';
    }
  }));
  return isNaN(num) ? 0 : num;
};

const GROUP_A_METRICS = [
  { label: "% Cited Papers", key: "pct_cited_papers" },
  { label: "% Top 10% Cited", key: "pct_top10_cited" },
  { label: "% Top 1% Cited", key: "pct_top1_cited" },
  { label: "Recent 5yr Share", key: "recent_5yr_share" }
];

const GROUP_B_METRICS = [
  { label: "H-Index", key: "h_index" },
  { label: "H5-Index", key: "h5_index" },
  { label: "H10-Index", key: "h10_index" },
  { label: "I10-Index", key: "i10_index" },
  { label: "G-Index", key: "g_index" }
];

const QualityBar = ({ entities, analysisRecords = [], isPeerMode }) => {
  const [hoveredEntityId, setHoveredEntityId] = useState(null);
  const [activeGroup, setActiveGroup] = useState('groupA');
  
  const currentData = useMemo(() => {
    const metrics = activeGroup === 'groupA' ? GROUP_A_METRICS : GROUP_B_METRICS;
    return metrics.map(m => {
      const row = { metric: m.label };
      entities.forEach(entity => {
        const record = analysisRecords.find(r => r.id === entity.id);
        row[entity.id] = parseNum(record?.[m.key]);
      });
      return row;
    });
  }, [activeGroup, entities, analysisRecords]);

  const CustomTooltip = ({ active, payload, label, coordinate }) => {
    if (active && payload && payload.length) {
      return (
        <PortalTooltip active={active} payload={payload} coordinate={coordinate}>
          <p className="font-bold text-slate-800 mb-2">{label}</p>
          {payload.map((entry, index) => {
            const entityId = entry.dataKey;
            const entity = entities.find(e => e.id == entityId);
            if (!entity) return null;
            const isHovered = hoveredEntityId == entityId;
            const isAnyHovered = hoveredEntityId !== null;
            const isBase = entity.isBaseEntity;
            
            let itemOpacity = 1;
            if (isPeerMode) {
              itemOpacity = (isBase || isHovered) ? 1 : 0.5;
            } else {
              itemOpacity = isAnyHovered ? (isHovered ? 1 : 0.5) : 1;
            }
            const shouldHighlight = isPeerMode ? (isBase || isHovered) : (isAnyHovered ? isHovered : false);
            
            return (
              <div key={index} className="flex items-center justify-between gap-4 mb-1" style={{ opacity: itemOpacity }}>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: entry.color }} />
                  <span style={{ color: entry.color, fontWeight: shouldHighlight ? 'bold' : 'normal' }}>{entry.name}</span>
                </div>
                <span className="font-medium" style={{ color: entry.color, fontWeight: shouldHighlight ? 'bold' : 'normal' }}>
                  {entry.value?.toLocaleString()}
                </span>
              </div>
            );
          })}
        </PortalTooltip>
      );
    }
    return null;
  };

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex flex-col xl:flex-row items-center justify-between gap-3 mb-4">
        <div className="text-center xl:text-left">
          <h3 className="text-lg font-semibold text-gray-800">Impact Quality Distribution</h3>
          <p className="text-xs text-slate-500">Comparing quality benchmarks across selected entities</p>
        </div>
        <div className="flex bg-slate-100 p-1 rounded-md shrink-0">
          <button 
            className={`px-3 py-1 text-xs font-medium rounded-sm transition-colors ${activeGroup === 'groupA' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
            onClick={() => setActiveGroup('groupA')}
          >
            Citation Quality (%)
          </button>
          <button 
            className={`px-3 py-1 text-xs font-medium rounded-sm transition-colors ${activeGroup === 'groupB' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
            onClick={() => setActiveGroup('groupB')}
          >
            Impact Indices
          </button>
        </div>
      </div>
      
      <div className="flex-1 w-full min-h-[250px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={currentData} margin={{ top: 10, right: 10, left: -20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
            <XAxis dataKey="metric" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#64748b" }} tickMargin={10} />
            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#64748b" }} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f8fafc' }} />
            <Legend 
              wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} 
              onMouseEnter={(e) => setHoveredEntityId(e.dataKey)}
              onMouseLeave={() => setHoveredEntityId(null)}
            />
            {entities.map(entity => {
              const isHovered = hoveredEntityId === entity.id;
              const isAnyHovered = hoveredEntityId !== null;
              
              let opacity = 1;
              let strokeW = 0;
              let strokeColor = "none";
              
              if (isPeerMode) {
                if (entity.isBaseEntity) {
                  opacity = 1;
                  strokeW = 1;
                  strokeColor = "#000";
                } else {
                  opacity = isHovered ? 1 : 0.25;
                  strokeW = isHovered ? 1 : 0;
                  strokeColor = isHovered ? "#000" : "none";
                }
              } else {
                opacity = 1;
                strokeW = 0;
                strokeColor = "none";
              }
              
              return (
              <Bar 
                key={entity.id} 
                dataKey={entity.id} 
                name={entity.name} 
                fill={entity.color} 
                fillOpacity={opacity}
                stroke={strokeColor}
                strokeWidth={strokeW}
                radius={[4, 4, 0, 0]} 
                maxBarSize={40} 
                onMouseEnter={() => setHoveredEntityId(entity.id)}
                onMouseLeave={() => setHoveredEntityId(null)}
              />
            )})}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default QualityBar;
